import { useMemo } from 'react'

import useSWR from 'swr'

import {
  basketFetcher,
  type BasketContentBlock,
  type BasketInput,
  type BasketResponse,
} from '@library/api-menu-experience'

type GroupedBasketRecipe = {
  id: string
  quantity: number
  numPortions: number
}

type UseGroupedBasketContentInput = {
  basketDate?: string
  basketRecipes: GroupedBasketRecipe[]
  enabled: boolean
  menuUuid?: string | null
  numPortions: number
  postcode?: string
}

export type GroupedBasketContentResult = {
  content?: BasketContentBlock[]
  error?: unknown
  isLoading: boolean
  isValidating: boolean
  useLegacyBasket?: boolean
}

type GroupedBasketKey = readonly ['grouped-basket-content', BasketInput]

const fetchGroupedBasketContent = ([, input]: GroupedBasketKey): Promise<BasketResponse> =>
  basketFetcher(input)

export const useFetchBasketContent = ({
  basketDate,
  basketRecipes,
  enabled,
  menuUuid,
  numPortions,
  postcode,
}: UseGroupedBasketContentInput): GroupedBasketContentResult => {
  const input = useMemo<BasketInput | null>(() => {
    if (!enabled || !menuUuid || !basketDate || !postcode) {
      return null
    }

    return {
      menuId: menuUuid,
      numPortions,
      deliveryDate: basketDate,
      postcode,
      recipes: basketRecipes
        .filter(({ quantity }) => quantity > 0)
        .map(({ id, numPortions: recipeNumPortions, quantity }) => ({
          id,
          numPortions: recipeNumPortions,
          quantity,
        })),
    }
  }, [basketDate, basketRecipes, enabled, menuUuid, numPortions, postcode])

  const key: GroupedBasketKey | null = input ? ['grouped-basket-content', input] : null
  const {
    data,
    error,
    isLoading: isSWRLoading,
    isValidating,
  } = useSWR(key, fetchGroupedBasketContent, {
    keepPreviousData: true,
  })

  return {
    content: data?.content,
    error,
    isLoading: isSWRLoading && !data,
    isValidating,
    useLegacyBasket: data?.useLegacyBasket,
  }
}
